import { current, produce } from 'immer'

import {
  extract_author,
  extract_automate,
  extract_end,
  extract_log,
  extract_message,
  extract_next_steps,
  extract_prompts,
  extract_thinking,
} from './stream.handler'

import { AiAnalystUtils } from '../../../agents/analyst/services/analyst.service'
import { AiAgent, AiPlan, MessageType } from '../../../common/ai.dictionary'
import { AiState } from '../../ai.state'

export const onGetAnswerEventStream = (state: AiState, { response }: { response: any }): AiState =>
  produce(state, (draft: AiState) => {
    if (!current(draft).backgroundAiResponse) {
      draft.streamingAiResponse = true
      draft.pendingAiResponse = true
    }
    const { message, context, sources } = response
    const lastMessageDraft = draft.messages[draft.messages.length - 1]
    const answer = extract_message(message)
    const thinking = extract_thinking(message)
    const nextSteps = extract_next_steps(message)
    const agents = extract_automate(message)
    const end = extract_end(message)
    const log = extract_log(message)
    const prompts = extract_prompts(message)
    const author = extract_author(message)

    if (message.answer?.operation === 'session') {
      draft.sessionId = message.answer.thread_id
      if (message.answer.request_id) {
        draft.lastRequestId = message.answer.request_id
      }
    }

    // parse logs and register
    if (log) {
      draft.logs.unshift({
        agent: log.agent,
        message: log.debug.info,
        message_type: 'INFO',
        timestamp: log.timestamp,
        status: log.debug.status,
        step: log.debug.step,
      })
      draft.currentAITaskMessage = log.debug.info
      return draft
    }
    if ((message as any).answer) {
      parseAnswerMessage(draft, message)
    }
    if (end) {
      parseEndMessage(draft, end)
      return draft
    }
    if (!answer && !nextSteps && !thinking) {
      return
    }
    const isLastMessageOfTypeAi =
      lastMessageDraft &&
      lastMessageDraft?.type === MessageType.AiMsg &&
      !lastMessageDraft?.commited
    if (isLastMessageOfTypeAi) {
      Object.assign(lastMessageDraft, response)
      lastMessageDraft.message = nextSteps + answer
      lastMessageDraft.context = lastMessageDraft.context || context
      lastMessageDraft.sources = sources
      lastMessageDraft.author = author
      lastMessageDraft.thinking = thinking
      lastMessageDraft.agents = agents
      lastMessageDraft.html_preview = null
      lastMessageDraft.prompts = prompts
      lastMessageDraft.type = MessageType.AiMsg
    } else {
      draft.messages.push({
        ...response,
        message,
        thinking,
        context,
        sources,
        prompts,
        agents,
        author,
        type: MessageType.AiMsg,
      })
    }
  })

export const onDeletePlan = (state: AiState): AiState =>
  produce(state, (draft: AiState) => {
    draft.selectedPlan = null
    draft.isPlanEnabled = false
  })

export const onUpdateStep = (
  state: AiState,
  { updateStep, agent }: { updateStep: any; agent: AiAgent }
): AiState =>
  produce(state, (draft: AiState) => {
    draft.selectedPlan?.actions.forEach((action) => {
      if (action.agent === agent) {
        action.steps = action.steps.map((step) =>
          step.step_number === updateStep.step_number ? { ...step, ...updateStep } : step
        )
      }
    })
  })

export const onRefineList = (state: AiState): AiState =>
  produce(state, (draft: AiState) => {
    draft.selectedPlan?.actions.forEach((action) => {
      if (action.agent === AiAgent.COORDINATOR) {
        action.steps = action.steps.map((step) => ({ ...step, step_status: 'default' }))
      }
    })
  })

export const onAutomateProcess = (state: AiState): AiState =>
  produce(state, (draft: AiState) => {
    draft.selectedPlan?.actions.forEach((action) => {
      if (action.agent === AiAgent.COORDINATOR) {
        action.steps = action.steps.map((step) => ({ ...step, step_status: 'default' }))
      }
    })
  })
export const onYesAction = (state: AiState): AiState =>
  produce(state, (draft: AiState) => {
    draft.messages.push({
      message: 'Yes, I approve the plan',
      type: MessageType.UserMsg,
      commited: true,
    })
    draft.pendingRouteToCoordinator = {
      prompt: 'yes',
      appId: null, // TODO: get appId from state
      appVersion: null, // TODO: get appVersion from state
    }
  })

export const onNoAction = (state: AiState): AiState =>
  produce(state, (draft: AiState) => {
    draft.messages.push({
      message: 'No, I do not approve the plan',
      type: MessageType.UserMsg,
      commited: true,
    })
    draft.pendingRouteToCoordinator = {
      prompt: 'no',
      appId: null, // TODO: get appId from state
      appVersion: null, // TODO: get appVersion from state
    }
  })

export const onSelectInterruptOption = (
  state: AiState,
  { option }: { option: { label: string; value: string } }
): AiState =>
  produce(state, (draft: AiState) => {
    draft.messages.push({
      message: option.label,
      type: MessageType.UserMsg,
      commited: true,
    })
  })

export const onInvokeStep = (
  state: AiState,
  { selectedStep, agent }: { selectedStep: any; agent: AiAgent }
): AiState =>
  produce(state, (draft: AiState) => {
    draft.messages.push({
      message:
        'Execute step using <b>AI ' +
        agent.toUpperCase() +
        '</b>:<br><pre class="text-xs bg-' +
        agent +
        ' rounded-xl"><b>' +
        selectedStep.step_name +
        '</b>\n\n' +
        selectedStep.step_description +
        '</pre>',
      type: MessageType.UserMsg,
      commited: true,
    })

    if (selectedStep.step_action === 'send_message') {
      draft.messages.push({
        message:
          '<div class="bg-neutral-900 text-sm p-2 rounded"> <i class="ph ph-info mr-1"></i> <b>Message sent successfully:</b> <br/>' +
          selectedStep.step_name +
          ' <br/> <br/>' +
          '</div>',
      })
    }
    if (selectedStep.action_name === 'receive_message') {
      draft.messages.push({
        message:
          '<div class="bg-neutral-900 text-sm p-2 rounded"> <i class="ph ph-info mr-1"></i> <b>File uploaded successfully:</b> <br/>' +
          selectedStep.step_name +
          ' <br/> <br/>' +
          '</div>',
      })
    }

    switch (selectedStep.parentAgent) {
      case AiAgent.ANALYST:
        draft.currentPlanStep = selectedStep
        draft.selectedAgent = AiAgent.ANALYST
        draft.pendingRouteToAnalyst = {
          prompt: selectedStep.recommended_prompt?.prompt ?? '',
          appId: state.appId,
          appVersion: state.appVersionId,
          processId: state.processId,
          processName: selectedStep.recommended_prompt?.name,
          processCode: selectedStep.recommended_prompt?.code,
          processDescription: selectedStep.recommended_prompt?.description,
        }
        draft.selectedPlan?.actions.forEach((action) => {
          if (action.agent === AiAgent.ANALYST) {
            action.steps = action.steps.map((step) =>
              step.step_number === draft.currentPlanStep?.step_number
                ? { ...step, step_status: 'running' }
                : step
            )
          }
        })
        break
      case AiAgent.DESIGNER:
        draft.currentPlanStep = selectedStep
        draft.selectedAgent = AiAgent.DESIGNER
        // Clear stale designer state from previous generation
        draft.generatedUI = null
        draft.generatedUINew = null
        draft.temporaryUI = null
        draft.generatedEditDiff = null
        draft.editDiffAppliedConfig = null
        draft.currentFullUI = null
        draft.generatedUIDataModelChanges = null
        draft.currentUI = null
        draft.pendingRouteToDesigner = {
          prompt: selectedStep.recommended_prompt?.prompt ?? '',
          appId: state.appId,
          appVersion: state.appVersionId,
          processId: state.processId,
          nodeId: selectedStep.step_destination?.id,
          areaId: selectedStep.step_destination?.areaId,
          swimlaneId: selectedStep.step_destination?.swimlaneDefinitionId,
        }
        draft.selectedPlan?.actions.forEach((action) => {
          if (action.agent === AiAgent.DESIGNER) {
            action.steps = action.steps.map((step) =>
              step.step_number === draft.currentPlanStep?.step_number
                ? { ...step, step_status: 'running' }
                : step
            )
          }
        })
        break
      case AiAgent.DEVELOPER:
        draft.currentPlanStep = selectedStep
        draft.selectedAgent = AiAgent.DEVELOPER
        draft.pendingRouteToDeveloper = {
          action_type: selectedStep.step_action,
          prompt: selectedStep.recommended_prompt?.prompt ?? '',
          appId: state.appId,
          appVersion: state.appVersionId,
          processId: state.processId,
          nodeId: selectedStep.step_destination?.id,
        }
        draft.selectedPlan?.actions.forEach((action) => {
          if (action.agent === AiAgent.DEVELOPER) {
            action.steps = action.steps.map((step) =>
              step.step_number === draft.currentPlanStep?.step_number
                ? { ...step, step_status: 'running' }
                : step
            )
          }
        })
        break
      case AiAgent.ARCHITECT:
        draft.currentPlanStep = selectedStep
        draft.selectedAgent = AiAgent.ARCHITECT
        draft.pendingRouteToArchitect = {
          prompt: selectedStep.recommended_prompt?.prompt ?? '',
          appId: state.appId,
          appVersion: state.appVersionId,
          processId: selectedStep.step_destination?.id || state.processId,
        }
        draft.selectedPlan?.actions.forEach((action) => {
          if (action.agent === AiAgent.ARCHITECT) {
            action.steps = action.steps.map((step) =>
              step.step_number === draft.currentPlanStep?.step_number
                ? { ...step, step_status: 'running' }
                : step
            )
          }
        })
        break
      case AiAgent.INTEGRATOR:
        draft.currentPlanStep = selectedStep
        draft.selectedAgent = AiAgent.INTEGRATOR
        draft.pendingRouteToIntegrator = {
          prompt: selectedStep.recommended_prompt?.prompt ?? '',
          agent_name: selectedStep.recommended_prompt?.name,
          agent_code: selectedStep.recommended_prompt?.code,
          agent_description: selectedStep.recommended_prompt?.description,
          appId: state.appId,
          appVersion: state.appVersionId,
          processId: state.processId,
        }
        draft.selectedPlan?.actions.forEach((action) => {
          if (action.agent === AiAgent.INTEGRATOR) {
            action.steps = action.steps.map((step) =>
              step.step_number === draft.currentPlanStep?.step_number
                ? { ...step, step_status: 'running' }
                : step
            )
          }
        })
        break
      default:
        break
    }
  })

const parseEndMessage = (draft: AiState, end: any): AiState => {
  if (end.operation === 'intent') {
    draft.reasoningLogs.unshift({
      agent: AiAgent.COORDINATOR,
      message: end.answer.intent.reasoning,
      message_type: 'REASONING',
      operation: 'CLASSIFY USER INTENT',
    })
    if (end.answer.intent.intent_classification === 'route_to_agent') {
      draft.selectedAgent = end.answer.intent.agent_name as AiAgent
      draft.generatedIntent = end.answer.intent
      switch (draft.selectedAgent) {
        case AiAgent.ASSISTANT:
          draft.pendingRouteToAssistant = {
            prompt: end.answer.intent.user_message,
            appId: draft.appId,
            appVersion: draft.appVersionId,
          }
          break
        case AiAgent.DESIGNER:
          draft.pendingRouteToDesigner = {
            prompt: end.answer.intent.user_message,
            appId: draft.appId,
            appVersion: draft.appVersionId,
            processId: draft.processId,
          }

          break
        case AiAgent.ANALYST:
          draft.pendingRouteToAnalyst = {
            prompt: end.answer.intent.user_message,
            appId: draft.appId,
            appVersion: draft.appVersionId,
            processId: draft.processId,
          }
          break
        case AiAgent.DEVELOPER:
          draft.pendingRouteToDeveloper = {
            prompt: end.answer.intent.user_message,
            appId: draft.appId,
            appVersion: draft.appVersionId,
            processId: draft.processId,
          }
          break
        case AiAgent.ARCHITECT:
          draft.pendingRouteToArchitect = {
            prompt: end.answer.intent.user_message,
            appId: draft.appId,
            appVersion: draft.appVersionId,
            processId: draft.processId,
          }
          break
        case AiAgent.INTEGRATOR:
          draft.pendingRouteToIntegrator = {
            prompt: end.answer.intent.user_message,
            agent_name: end.answer.intent.agent_name,
            appId: draft.appId,
            appVersion: draft.appVersionId,
            processId: draft.processId,
          }
          break
        case AiAgent.AUDITOR:
          draft.pendingRouteToAuditor = {
            prompt: end.answer.intent.user_message,
            appId: draft.appId,
            appVersion: draft.appVersionId,
            processId: draft.processId,
          }
          break
        default:
          break
      }
    }
    if (end.answer.intent.intent_classification === 'unknown_intent') {
      draft.messages.push({
        message:
          '<br/> <div class="bg-neutral-900 text-sm p-2 rounded"> <i class="ph ph-question mr-1"></i> <span class="font-bold">I need more information:</span> <br/><br/>' +
          end.answer.intent.more_information +
          '</div> <br/>',
        type: MessageType.AiMsg,
        commited: true,
        actions: {
          interruptOptions: end.answer.intent.options || null,
        },
      })
      draft.generatedIntent = end.answer.intent
      draft.streamingAiResponse = false
      draft.pendingAiResponse = false
      draft.currentAITaskMessage = null
    }
    if (end.answer.intent.intent_classification === 'agent_proposal') {
      if (end.answer.intent.action_required) {
        let agents = '<br/><br/><br/>'
        if (end.answer.intent.examples) {
          agents += end.answer.intent.examples
            .map(
              (example, index) =>
                `<div class="flex flex-col m-2 text-neutral-200"><b>${index + 1}. ${example.agent}</b> - ${example.description}</div>`
            )
            .join('<br/><br/>')
        }
        // Use options from backend, or fallback to converting examples into options
        const interruptOptions =
          end.answer.intent.options ||
          (end.answer.intent.examples
            ? end.answer.intent.examples.map((example: any) => ({
                label: example.agent + ' - ' + example.description,
                value: example.agent,
              }))
            : null)
        draft.messages.push({
          message:
            '<br/> <div class="bg-neutral-900 text-sm p-2 rounded"> <i class="ph ph-question mr-1"></i> <span class="font-bold">I need more information:</span> <br/><br/>' +
            end.answer.intent.more_information +
            agents +
            '</div> <br/>',
          type: MessageType.AiMsg,
          commited: true,
          actions: {
            interruptOptions,
          },
        })
        draft.waitingForHumanApproval = true
      } else {
        // draft.generatedActions = end.answer.intent.actions
        draft.waitingForHumanApproval = false
        draft.generatedActions = end.answer.intent.actions
        draft.selectedAgent = AiAgent.COORDINATOR
        draft.pendingRouteToCoordinator = {
          prompt: end.answer.intent.rephrased_input,
          plan_type: 'agent',
          appId: draft.appId,
          appVersion: draft.appVersionId,
        }
      }
      draft.selectedAgent = AiAgent.COORDINATOR
      draft.pendingRouteToCoordinator = null
      draft.generatedIntent = end.answer.intent
      draft.streamingAiResponse = false
      draft.pendingAiResponse = false
      draft.currentAITaskMessage = null
    }
    if (end.answer.intent.intent_classification === 'generate_plan') {
      if (end.answer.intent.action_required) {
        draft.messages.push({
          message:
            '<br/> <div class="bg-neutral-900 text-sm p-2 rounded"> <i class="ph ph-question mr-1"></i> <span class="font-bold">I need more information:</span> <br/><br/>' +
            end.answer.intent.more_information +
            '</div> <br/>',
          type: MessageType.AiMsg,
          commited: true,
          actions: {
            interruptOptions: end.answer.intent.options || null,
          },
        })
        draft.generatedActions = end.answer.intent.actions
        draft.waitingForHumanApproval = true
        draft.pendingRouteToCoordinator = null
        draft.generatedIntent = end.answer.intent
        draft.streamingAiResponse = false
        draft.pendingAiResponse = false
        draft.currentAITaskMessage = null
      } else {
        draft.waitingForHumanApproval = false
        draft.generatedActions = end.answer.intent.actions.filter(
          (action) =>
            action.action_name !== 'generate_agent_workflow' &&
            action.action_name !== 'create_agent'
        )
        draft.selectedAgent = AiAgent.COORDINATOR
        draft.pendingRouteToCoordinator = {
          plan_type: end.answer.intent.plan_type,
          prompt: end.answer.intent.rephrased_input,
          appId: draft.appId,
          appVersion: draft.appVersionId,
        }
        draft.streamingAiResponse = false
        draft.pendingAiResponse = false
        draft.currentAITaskMessage = null
      }
    }
    if (end.answer.intent.intent_classification === 'generate_actions') {
      if (end.answer.intent.action_required) {
        draft.messages.push({
          message:
            '<br/> <div class="bg-neutral-900 text-sm p-2 rounded"> <i class="ph ph-question mr-1"></i> <span class="font-bold">I need more information:</span> <br/><br/>' +
            end.answer.intent.more_information +
            '</div> <br/>',
          type: MessageType.AiMsg,
          commited: true,
          actions: {
            interruptOptions: end.answer.intent.options || null,
          },
        })
        draft.waitingForHumanApproval = true
      } else {
        draft.generatedActions = end.answer.intent.actions
      }
      draft.selectedAgent = AiAgent.COORDINATOR
      draft.pendingRouteToCoordinator = null
      draft.generatedIntent = end.answer.intent
      draft.streamingAiResponse = false
      draft.pendingAiResponse = false
      draft.currentAITaskMessage = null
    }
  }

  if (end.operation === 'new_plan') {
    draft.pendingRouteToCoordinator = null
    draft.generatingPlan = true
    draft.isPlanEnabled = true
    draft.generatedIntent = null
  }
  if (end.operation === 'generated_prompt') {
    draft.reasoningLogs.unshift({
      agent: AiAgent.COORDINATOR,
      message: end.answer.generated_prompt.reasoning,
      message_type: 'REASONING',
      operation: 'GENERATED PROMPT',
    })
  }
  if (end.operation === 'next_steps') {
    draft.reasoningLogs.unshift({
      agent: AiAgent.COORDINATOR,
      message: end.answer.next_steps.reasoning,
      message_type: 'REASONING',
      operation: 'NEXT STEPS',
    })
    draft.pendingRouteToCoordinator = null
    draft.currentAITaskMessage = null
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.currentPlanStep = null
    draft.messages[draft.messages.length - 1].commited = true
  }
  if (end.operation === 'automate') {
    draft.reasoningLogs.unshift({
      agent: AiAgent.COORDINATOR,
      message: end.answer.automate.reasoning,
      message_type: 'REASONING',
      operation: 'AUTOMATE',
    })
    draft.pendingRouteToCoordinator = null
    draft.currentAITaskMessage = null
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.currentPlanStep = null
    draft.messages[draft.messages.length - 1].commited = true
  }
  if (end.operation === 'map_destination') {
    end.answer.map_destinations?.destinations?.forEach((destination) => {
      draft.selectedPlan?.actions.forEach((action) => {
        if (action.agent === destination.agent) {
          const nodeDetails = AiAnalystUtils.getNodeUIDetails(destination.id)
          action.steps = action.steps.map((step) =>
            step.step_number === destination?.step_number
              ? {
                  ...step,
                  step_destination: {
                    type: 'Node',
                    name: destination.name,
                    id: destination.id,
                    swimlaneDefinitionId: nodeDetails.swimlaneDefinitionId,
                    areaId: nodeDetails.navigationAreaId,
                  },
                }
              : step
          )
        }
      })
    })
    draft.reasoningLogs.unshift({
      agent: AiAgent.COORDINATOR,
      message: end.answer.map_destinations.reasoning,
      message_type: 'REASONING',
      operation: 'MAP DESTINATION NODE',
    })
    draft.pendingRouteToCoordinator = null
    draft.currentAITaskMessage = null
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.currentPlanStep = null
    draft.messages[draft.messages.length - 1].commited = true
  }
  if (end.operation === 'generate_prompt') {
    draft.selectedPlan?.actions.forEach((action) => {
      if (action.agent === end.answer.agent) {
        action.steps = action.steps.map((step) =>
          step.step_number === end.answer.step?.step_number
            ? { ...step, recommended_prompt: end.answer.generated_prompt }
            : step
        )
      }
    })
  }
  if (end.operation === 'generated_agent') {
    draft.generatedAgentWorkflow = { ...end.answer.generated_agent, isFinal: true }
    draft.pendingRouteToIntegrator = null
    draft.messages.push({
      message: end.answer.generated_agent.description,
      type: MessageType.AiMsg,
      commited: true,
    })
  }
  if (end.operation === 'check_context') {
    if (end.answer.check_context?.type === 'new_plan') {
      draft.messages.push({
        message:
          '<div class="bg-red-700 text-sm p-2 rounded"> <i class="ph ph-warning mr-1"></i> <b>Based on your current context:</b> <br/>' +
          end.answer.check_context?.message +
          '</div> <br/> <br/> ' +
          end.answer.check_context?.user_question,
        type: MessageType.AiMsg,
        commited: true,
        actions: { yesNo: true },
      })
    } else if (end.answer.check_context?.type === 'adapt_plan') {
      draft.messages.push({
        message:
          '<div class="bg-neutral-900 text-sm p-2 rounded"> <i class="ph ph-info mr-1"></i> <b>Based on your current context:</b> <br/>' +
          end.answer.check_context?.message +
          '</div>',
        type: MessageType.AiMsg,
        commited: true,
      })
    }

    draft.reasoningLogs.unshift({
      agent: AiAgent.COORDINATOR,
      message: end.answer.check_context.reasoning,
      message_type: 'REASONING',
      operation: 'CHECK CONTEXT',
    })

    draft.pendingRouteToCoordinator = null
    draft.currentAITaskMessage = null
  }
  if (end.operation === 'detail_plan') {
    draft.generatingPlan = true
    draft.isPlanEnabled = true
    draft.pendingRouteToCoordinator = null
    draft.generatedIntent = null
    draft.selectedPlan = extract_plan(draft, end.answer.plan, 'Prompt')
    draft.reasoningLogs.unshift({
      agent: AiAgent.COORDINATOR,
      message: end.answer.plan.reasoning,
      message_type: 'REASONING',
      operation: 'GENERATE PLAN',
    })
    draft.generatingPlan = false
  }
  if (end.operation === 'close_message') {
    draft.pendingRouteToCoordinator = null
    draft.messages.push({
      message: end.answer.close_message,
      type: MessageType.AiMsg,
      commited: true,
    })
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.currentAITaskMessage = null
    draft.generatedIntent = null
  }
  if (end.operation === 'other_options') {
    draft.pendingRouteToCoordinator = null
    draft.messages.push({
      message: end.answer.other_options,
      type: MessageType.AiMsg,
      commited: true,
    })
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.currentAITaskMessage = null
    draft.generatedIntent = null
  }

  if (end.operation === 'human_approval_requested') {
    draft.waitingForHumanApproval = true
    draft.pendingRouteToCoordinator = null
    draft.generatedIntent = end.answer.intent
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.currentAITaskMessage = null
  }
  if (end.operation === 'human_approval_received') {
    draft.waitingForHumanApproval = false
    draft.currentAITaskMessage = null
  }

  if (end.operation === 'human_feedback_requested') {
    draft.waitingForHumanApproval = true
    draft.pendingRouteToCoordinator = null
    draft.generatedIntent = end.answer?.human_approval?.generated_output
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.currentAITaskMessage = null
  }
  if (end.operation === 'human_feedback_received') {
    draft.waitingForHumanApproval = false
    draft.currentAITaskMessage = null
  }
  return draft
}

const parseAnswerMessage = (draft: AiState, message: any): AiState => {
  if (message.answer.operation === 'plan') {
    draft.isPlanEnabled = true
    draft.pendingRouteToCoordinator = null
    draft.selectedPlan = extract_plan(draft, message.answer.plan, 'Prompt')
  }
  if (message.answer.operation === 'generate_prompt') {
    draft.generatedPrompt = message.answer.generated_prompt
  }
  if (message.answer.operation === 'generated_agent') {
    draft.generatedAgentWorkflow = message.answer.generated_agent
  }
  return draft
}

const extract_plan = (draft: AiState, plan: any, source_type: 'Prompt' | 'Document'): AiPlan => {
  for (const action of plan.actions) {
    for (const step of action.steps) {
      step.parentAgent = action.agent
      if (action.agent === AiAgent.ANALYST) {
        step.step_destination = {
          routerParams: [
            'projects',
            draft.appId,
            'config',
            draft.appVersionId,
            'processes',
            draft.processId,
            'diagram',
          ],
          type: 'Process',
          name: draft.processName || 'CurrentProcess',
          id: draft.processId,
        }
      }
      if (action.agent === AiAgent.INTEGRATOR) {
        step.step_destination = {
          routerParams: [
            'projects',
            draft.appId,
            'config',
            draft.appVersionId,
            'agent-builder',
            'agents',
          ],
          type: 'Project',
          name: draft.appName || 'CurrentProject',
          id: draft.appId,
        }
      }
      if (action.agent === AiAgent.ARCHITECT) {
        step.step_destination = {
          routerParams: [
            'projects',
            draft.appId,
            'config',
            draft.appVersionId,
            'processes',
            draft.processId,
            'data-model',
          ],
          type: 'Process',
          name: draft.processName || 'CurrentProcess',
          id: draft.processId,
        }
      }
      if (!step.step_source) {
        if (source_type === 'Prompt') {
          step.step_source = {
            type: 'Prompt',
            name: 'User Prompt',
          }
        } else if (source_type === 'Document') {
          step.step_source = {
            type: 'Document',
            name: 'User Document',
          }
        }
      }
    }
  }
  return plan
}

export const onGeneratedPromptForAgentSuccess = (
  state: AiState,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _args: { response: any; action_name: string }
): AiState =>
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  produce(state, (_draft: AiState) => {})

export const onGeneratedActionHandler = (
  state: AiState,
  { response_action, action_name }: { response_action: any; action_name: string }
): AiState =>
  produce(state, (draft: AiState) => {
    if (action_name === 'create_process') {
      draft.messages.push({
        message:
          '<div class="bg-neutral-900 text-sm p-2 rounded"> <i class="ph ph-info mr-1"></i> <b>Process created successfully:</b> <br/>' +
          response_action.resourceName +
          ' <br/> <br/> <a href="/projects/' +
          state.appId +
          '/config/' +
          state.appVersionId +
          '/processes/' +
          response_action.resourceId +
          '/diagram">View Created Process</a>' +
          ' <br/> <br/> <a href="/projects/' +
          state.appId +
          '/config/' +
          state.appVersionId +
          '/processes">View All Processes</a>' +
          '</div>',
        type: MessageType.AiMsg,
        commited: true,
      })
    }
    if (action_name === 'create_project') {
      draft.appId = response_action.id
      draft.appVersionId = response_action.mainFirstCommitId
      draft.messages.push({
        message:
          '<div class="bg-neutral-900 text-sm p-2 rounded"> <i class="ph ph-info mr-1"></i> <b>Project created successfully:</b> <br/>' +
          response_action.name +
          ' <br/> <br/> <a href="/projects/' +
          response_action.id +
          '/config/' +
          response_action.mainFirstCommitId +
          '">View Created Project</a>' +
          '</div>',
        type: MessageType.AiMsg,
        commited: true,
      })
    }
    if (action_name === 'create_agent') {
      draft.messages.push({
        message:
          '<div class="bg-neutral-900 text-sm p-2 rounded"> <i class="ph ph-info mr-1"></i> <b>Agent created successfully:</b> <br/>' +
          response_action.name +
          ' <br/> <br/> <a href="/projects/' +
          state.appId +
          '/config/' +
          state.appVersionId +
          '/agent-builder/agents/' +
          response_action.id +
          '/editor' +
          '">View Created Agent</a>' +
          '</div>',
        type: MessageType.AiMsg,
        commited: true,
      })
    }
  })
